/* Theme-adapted configurable multipack selector. Prices from bundle tags are display-only. */
class PackageBuilder extends HTMLElement {
  connectedCallback() {
    const configElement = this.querySelector('[data-package-builder-config]');
    if (!configElement || this.dataset.initialized === 'true') return;

    try {
      this.config = JSON.parse(configElement.textContent);
    } catch (error) {
      console.warn('Package builder configuration could not be read.', error);
      return;
    }

    this.offers = this.getOffers(this.config.tags || []);
    if (!this.offers.length || !this.config.product?.variants?.length) return;

    this.dataset.initialized = 'true';
    this.product = this.config.product;
    this.labels = this.getLabels();
    this.sectionId = this.dataset.sectionId;
    this.productRoot = this.closest('product-info') || document;
    if (this.productRoot !== document) this.productRoot.classList.add('package-builder-enabled');
    this.variantSelects = this.productRoot.querySelector(`variant-selects[data-section="${CSS.escape(this.sectionId)}"]`);
    this.form = document.getElementById(`product-form-${this.sectionId}`);
    if (!this.form) return;

    this.nativeInputs = this.variantSelects ? [...this.variantSelects.querySelectorAll('.product-form__input')] : [];
    this.activeOffer = this.offers[0];
    this.render();
    this.addEventListener('click', this.onClick.bind(this));
    this.form.addEventListener('submit', this.onSubmit.bind(this), true);
  }

  getOffers(tags) {
    return tags.map((tag) => {
      const match = String(tag).trim().match(/^bundle:(\d+):(\d+(?:\.\d+)?)(?::(\d+))?$/i);
      if (!match) return null;
      return { quantity: Number(match[1]), price: Number(match[2]), selectionCount: Number(match[3] || match[1]) };
    }).filter((offer) => offer && offer.quantity > 0 && offer.selectionCount > 0 && offer.price >= 0)
      .sort((a, b) => a.quantity - b.quantity);
  }

  getLabels() {
    const language = String(this.config.locale || document.documentElement.lang || 'en').toLowerCase().split('-')[0];
    const translations = {
      zh: { package: '套餐', item: '商品', unit: '件', packageItem: '套餐商品', chooseAvailable: '请为每件商品选择可售规格。', adding: '正在加入购物车…', failed: '套餐加入购物车失败。', buyNow: '立即购买' },
      ro: { package: 'Pachet', item: 'Articol', unit: 'BUC.', packageItem: 'Articol pachet', chooseAvailable: 'Alegeți o opțiune disponibilă pentru fiecare articol.', adding: 'Se adaugă…', failed: 'Pachetul nu a putut fi adăugat.' },
      de: { package: 'Paket', item: 'Artikel', unit: 'STK.', packageItem: 'Paketartikel', chooseAvailable: 'Wählen Sie für jeden Artikel eine verfügbare Option.', adding: 'Wird hinzugefügt…', failed: 'Das Paket konnte nicht hinzugefügt werden.' },
      fr: { package: 'Lot', item: 'Article', unit: 'PCS', packageItem: 'Article du lot', chooseAvailable: 'Choisissez une option disponible pour chaque article.', adding: 'Ajout en cours…', failed: 'Le lot n’a pas pu être ajouté.' },
      es: { package: 'Paquete', item: 'Artículo', unit: 'UDS.', packageItem: 'Artículo del paquete', chooseAvailable: 'Elija una opción disponible para cada artículo.', adding: 'Añadiendo…', failed: 'No se pudo añadir el paquete.', buyNow: 'Comprar ahora' },
      it: { package: 'Pacchetto', item: 'Articolo', unit: 'PZ.', packageItem: 'Articolo del pacchetto', chooseAvailable: 'Scegli un’opzione disponibile per ogni articolo.', adding: 'Aggiunta…', failed: 'Impossibile aggiungere il pacchetto.' },
      nl: { package: 'Pakket', item: 'Artikel', unit: 'ST.', packageItem: 'Pakketartikel', chooseAvailable: 'Kies voor elk artikel een beschikbare optie.', adding: 'Toevoegen…', failed: 'Het pakket kon niet worden toegevoegd.' },
      pl: { package: 'Pakiet', item: 'Produkt', unit: 'SZT.', packageItem: 'Produkt w pakiecie', chooseAvailable: 'Wybierz dostępną opcję dla każdego produktu.', adding: 'Dodawanie…', failed: 'Nie udało się dodać pakietu.' }
    };
    return translations[language] || { package: 'Package', item: 'Item', unit: 'PCS', packageItem: 'Package item', chooseAvailable: 'Please choose an available option for every item.', adding: 'Adding…', failed: 'Package could not be added.', buyNow: 'Buy now' };
  }

  money(value) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: this.config.currency || 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0
      }).format(value);
    } catch (_) {
      return `${value} ${this.config.currency || ''}`.trim();
    }
  }

  escape(value) {
    const element = document.createElement('span');
    element.textContent = value;
    return element.innerHTML;
  }

  variantImage(optionIndex, value) {
    const variants = this.product.variants.filter((variant) => variant.options[optionIndex] === value);
    const variant = variants.find((item) => item.available && (item.featured_image || item.image || item.featured_media))
      || variants.find((item) => item.featured_image || item.image || item.featured_media);
    const image = variant?.featured_image || variant?.image || variant?.featured_media?.preview_image;
    if (typeof image === 'string') return image;
    return image?.src || image?.url || '';
  }

  optionUsesImages(optionIndex, values) {
    const images = values.map((value) => this.variantImage(optionIndex, value)).filter(Boolean);
    return images.length === values.length && new Set(images).size > 1;
  }

  offerLabel(offer) {
    return `${offer.quantity} ${this.labels.unit}`;
  }

  get startingVariant() {
    const selectedId = Number(this.form?.querySelector('[name="id"]')?.value);
    return this.product.variants.find((variant) => variant.id === selectedId && variant.available)
      || this.variantSelects?.currentVariant
      || this.product.variants.find((variant) => variant.available)
      || this.product.variants[0];
  }

  render() {
    this.hidden = false;
    this.innerHTML = `
      <fieldset class="package-picker" aria-label="${this.labels.package}">
        <legend class="form__label">${this.labels.package}</legend>
        <div class="package-picker__offers">${this.offers.map((offer) => `
          <button type="button" class="package-card ${offer === this.activeOffer ? 'is-selected' : ''}" data-package-index="${this.offers.indexOf(offer)}" aria-pressed="${offer === this.activeOffer}">
            <span class="package-card__radio" aria-hidden="true"></span>
            <span class="package-card__title">${this.offerLabel(offer)}</span>
            <span class="package-card__price">${this.money(offer.price)}</span>
          </button>`).join('')}</div>
      </fieldset>
      <div class="package-builder__components"></div>`;
    this.setMode();
  }

  setMode() {
    const multipack = this.activeOffer.quantity > 1;
    this.nativeInputs.forEach((input) => input.classList.add('package-builder__native-options-hidden'));
    this.variantSelects?.classList.add('package-builder__native-options-hidden');
    this.form.closest('product-form')?.classList.toggle('package-builder-active', multipack);
    this.ensureDirectCheckoutButton(multipack);
    this.renderComponents();
  }

  ensureDirectCheckoutButton(show) {
    const buttons = this.form.querySelector('.product-form__buttons');
    if (!buttons) return;
    let button = buttons.querySelector('.package-builder__buy-now');
    if (!show) {
      button?.remove();
      return;
    }
    if (!button) {
      button = document.createElement('button');
      button.type = 'button';
      button.className = 'package-builder__buy-now button button--full-width button--primary';
      button.textContent = this.labels.buyNow || 'Buy now';
      button.addEventListener('click', () => this.addPackage(true));
      buttons.appendChild(button);
    }
  }

  renderComponents() {
    const values = this.startingVariant.options || [];
    const options = this.product.options || [];
    this.querySelector('.package-builder__components').innerHTML = Array.from({ length: this.activeOffer.selectionCount }, (_, componentIndex) => `
      <fieldset class="package-builder__component" data-component-index="${componentIndex}">
        <legend class="form__label">${this.labels.item} ${componentIndex + 1}</legend>
        ${options.map((optionName, optionIndex) => {
          const optionValues = [...new Set(this.product.variants.map((variant) => variant.options[optionIndex]))];
          const usesImages = this.optionUsesImages(optionIndex, optionValues);
          return `
            <div class="package-builder__option ${usesImages ? 'package-builder__option--images' : ''}" data-option-index="${optionIndex}">
              <span class="package-builder__option-label">${this.escape(optionName)}</span>
              <div class="package-builder__values">${optionValues.map((value) => {
                const image = usesImages ? this.variantImage(optionIndex, value) : '';
                return `
                  <button type="button" class="package-builder__value ${image ? 'package-builder__value--image' : ''} ${value === values[optionIndex] ? 'is-selected' : ''}" data-value="${this.escape(value)}" aria-label="${this.escape(value)}">
                    ${image ? `<img src="${this.escape(image)}" alt="" width="72" height="72" loading="lazy">` : ''}
                    <span>${this.escape(value)}</span>
                  </button>`;
              }).join('')}</div>
            </div>`;
        }).join('')}
      </fieldset>`).join('');
  }

  onClick(event) {
    const offerButton = event.target.closest('[data-package-index]');
    if (offerButton) {
      this.activeOffer = this.offers[Number(offerButton.dataset.packageIndex)];
      this.render();
      return;
    }
    const valueButton = event.target.closest('.package-builder__value');
    if (valueButton) {
      valueButton.parentElement.querySelectorAll('.package-builder__value').forEach((button) => button.classList.remove('is-selected'));
      valueButton.classList.add('is-selected');
      if (this.activeOffer.quantity === 1) this.syncSingleSelection();
    }
  }

  syncSingleSelection() {
    if (!this.variantSelects) return;
    const component = this.querySelector('.package-builder__component');
    const selections = [...component.querySelectorAll('.package-builder__option')].map((option) => option.querySelector('.package-builder__value.is-selected')?.dataset.value);
    const fieldsets = [...this.variantSelects.querySelectorAll('.product-form__input')];
    let changedControl;

    fieldsets.forEach((fieldset, index) => {
      const select = fieldset.querySelector('select');
      if (select && select.value !== selections[index]) {
        select.value = selections[index];
        changedControl = select;
        return;
      }
      const radio = [...fieldset.querySelectorAll('input[type="radio"]')].find((input) => input.value === selections[index]);
      if (radio && !radio.checked) {
        radio.checked = true;
        changedControl = radio;
      }
    });

    changedControl?.dispatchEvent(new Event('change', { bubbles: true }));
  }

  selectedVariants() {
    return [...this.querySelectorAll('.package-builder__component')].map((component) => {
      const selections = [...component.querySelectorAll('.package-builder__option')].map((option) => option.querySelector('.package-builder__value.is-selected')?.dataset.value);
      return this.product.variants.find((variant) => variant.available && variant.options.every((value, index) => value === selections[index]));
    });
  }

  onSubmit(event) {
    if (this.activeOffer.quantity === 1 || this.adding) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.addPackage();
  }

  async addPackage(goToCheckout = false) {
    if (this.adding) return;
    const variants = this.selectedVariants();
    if (variants.some((variant) => !variant)) {
      window.alert(this.labels.chooseAvailable);
      return;
    }

    this.adding = true;
    const submit = this.form.querySelector('[type="submit"]');
    const directCheckout = this.form.querySelector('.package-builder__buy-now');
    const submitText = submit?.querySelector('span');
    const originalText = submitText?.textContent;
    if (submit) {
      submit.setAttribute('aria-disabled', 'true');
      submit.classList.add('loading');
      const text = submit.querySelector('span');
      if (text) text.textContent = this.labels.adding;
    }
    if (directCheckout) directCheckout.setAttribute('aria-disabled', 'true');

    const packageId = `bundle-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const items = variants.map((variant, index) => ({
      id: variant.id,
      quantity: 1,
      properties: {
        [this.labels.package]: this.offerLabel(this.activeOffer),
        [this.labels.packageItem]: `${index + 1} / ${this.activeOffer.selectionCount}`,
        _package_id: packageId
      }
    }));

    try {
      const response = await fetch(this.config.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || this.labels.failed);
      window.location.assign(goToCheckout ? '/checkout' : (this.config.cartUrl || '/cart'));
    } catch (error) {
      console.error(error);
      window.alert(error.message || this.labels.failed);
    } finally {
      this.adding = false;
      if (submit) {
        submit.removeAttribute('aria-disabled');
        submit.classList.remove('loading');
        const text = submit.querySelector('span');
        if (text && originalText) text.textContent = originalText.trim();
      }
      directCheckout?.removeAttribute('aria-disabled');
    }
  }
}

if (!customElements.get('package-builder')) customElements.define('package-builder', PackageBuilder);
